import { FC } from "react";

import { Icon } from "atoms/icon/Icon";
import AiStartIcon from "atoms/icon/icons/ai-start-icon.svg";
import Chevron from "atoms/icon/icons/chevron_right.svg";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { useUser } from "modules/user/useUser/useUser";

import { replacePersonaPlaceholders } from "../adonis.helpers";
import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";
import adonisImage from "../assets/adonis-shop-item.webp";
import ariaImage from "../assets/aria-shop-item.webp";

interface AdvisorShopCategoryLinkText {
  title: string;
  subtitle: string;
  badge_text: string;
}

export interface AdvisorShopCategoryLinkProps {
  linkText?: AdvisorShopCategoryLinkText;
  onClick: () => void;
  background?: string;
  className?: string;
}

const PREDEFINED_IMAGES: Record<string, string> = {
  Adonis: adonisImage,
  Aria: ariaImage,
};

export const AdvisorShopCategoryLink: FC<AdvisorShopCategoryLinkProps> = ({
  linkText,
  onClick,
  background = "radial-gradient(circle at 30% 30%, #DEC19C 0%, #E8D5C0 70%, #F0E5D8 100%)",
  className = "",
}) => {
  const { persona } = useAdonisChat();
  const { user } = useUser();

  const username = user?.firstName ?? "";

  const defaultText: AdvisorShopCategoryLinkText = {
    title: "Just for {username}",
    subtitle: "Recommended for you by {Name}",
    badge_text: "NEW PICKS",
  };

  const mergedText: AdvisorShopCategoryLinkText = {
    title: linkText?.title ?? defaultText.title,
    subtitle: linkText?.subtitle ?? defaultText.subtitle,
    badge_text: linkText?.badge_text ?? defaultText.badge_text,
  };

  const text: AdvisorShopCategoryLinkText = {
    title: replacePersonaPlaceholders(
      mergedText.title.replace("{username}", username),
      persona
    ),
    subtitle: replacePersonaPlaceholders(mergedText.subtitle, persona),
    badge_text: replacePersonaPlaceholders(mergedText.badge_text, persona),
  };

  const imageUrl = PREDEFINED_IMAGES[persona.name] || adonisImage;

  return (
    <button
      type="button"
      className={`relative flex h-[120px] w-full cursor-pointer items-center justify-between rounded-lg pr-3 shadow ${className}`}
      style={{ background }}
      onClick={onClick}
    >
      <div className="absolute left-1 top-1 rounded-full bg-white p-[5px]">
        <Icon svg={AiStartIcon} size="x3small" color="primaryText" />
      </div>
      <img
        src={imageUrl}
        alt={text.title}
        className="aspect-square h-full rounded-l-lg object-cover pr-4"
      />
      <div className="flex-grow items-center pr-3 text-left">
        <div className="pb-1">
          <DescriptiveTag color="light" size="small">
            {text.badge_text}
          </DescriptiveTag>
        </div>
        <div className="pb-2 font-serif text-h3 text-[rgba(27,27,27,1)]">
          {text.title}
        </div>
        <div className="font-sans text-body2 text-[rgba(37,37,37,1)]">
          {text.subtitle}
        </div>
      </div>
      <div className="items-center fill-brand-color">
        <Chevron className="h-6 w-6" />
      </div>
    </button>
  );
};
